/**
 * ConfigCompilerPage — Compile, Preview & Publish DynamicConfig
 *
 * Shows config sources (menu, capabilities, feature flags, templates, policies),
 * lets admin compile and preview the resulting config, then publish.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Upload,
  Eye, BarChart3, Settings2, Shield, FileText, ToggleLeft,
} from 'lucide-react';
import { api } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SourceStatus {
  source: string;
  status: 'ok' | 'empty' | 'error';
  count: number;
  error?: string;
}

interface CompileResult {
  config: Record<string, unknown>;
  validation: { valid: boolean; errors: string[] };
  sources: SourceStatus[];
  compiledAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SOURCE_META: Record<string, { label: string; icon: React.ElementType; description: string }> = {
  menu_config:       { label: 'Menu Config',        icon: Settings2,  description: 'Navigation structure, categories, mobile style' },
  capabilities:      { label: 'Capabilities',       icon: Shield,     description: 'Roles, permissions, enabled skills' },
  feature_flags:     { label: 'Feature Flags',      icon: ToggleLeft, description: 'Per-tenant feature toggles' },
  document_templates:{ label: 'Document Templates', icon: FileText,   description: 'Active document templates (invoice, BL, DO, etc.)' },
  policies:          { label: 'Policies',           icon: BarChart3,  description: 'Policy rules for decision boundaries' },
};

function StatusIcon({ status }: { status: 'ok' | 'empty' | 'error' }) {
  if (status === 'ok')    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  if (status === 'empty') return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
  return <XCircle className="h-4 w-4 text-red-500" />;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ConfigCompilerPage() {
  const [sources, setSources] = useState<SourceStatus[]>([]);
  const [compiled, setCompiled] = useState<CompileResult | null>(null);
  const [loadingSources, setLoadingSources] = useState(true);
  const [compiling, setCompiling] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<{ version: number } | null>(null);
  const [tab, setTab] = useState('sources');

  const loadSources = useCallback(async () => {
    setLoadingSources(true);
    try {
      const res = await api.get<{ sources: SourceStatus[] }>('/config/sources');
      setSources(res?.sources ?? []);
    } catch {
      setSources([]);
    } finally {
      setLoadingSources(false);
    }
  }, []);

  useEffect(() => { loadSources(); }, [loadSources]);

  const handleCompile = async () => {
    setCompiling(true);
    setPublishResult(null);
    try {
      const res = await api.post<CompileResult>('/config/compile', { channel: 'final' });
      if (res) {
        setCompiled(res);
        setTab('preview');
      }
    } catch {
      // handled by DataErrorBanner
    } finally {
      setCompiling(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await api.post<{ version: number }>('/config/compile/apply', { channel: 'final' });
      if (res) {
        setPublishResult(res);
        setTab('sources');
        loadSources();
      }
    } catch {
      // handled by error display
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Config Compiler</h2>
          <p className="text-xs text-muted-foreground">
            Compile, preview and publish tenant configuration from all sources
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={loadSources} disabled={loadingSources}>
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loadingSources ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={handleCompile} disabled={compiling}>
            {compiling ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Eye className="mr-1 h-3.5 w-3.5" />}
            Compile Preview
          </Button>
        </div>
      </div>

      {publishResult && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
          <CardContent className="py-3 px-4 flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
            <CheckCircle2 className="h-4 w-4" />
            Config published as version <strong>{publishResult.version}</strong>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-8 text-xs">
          <TabsTrigger value="sources">Sources ({sources.length})</TabsTrigger>
          <TabsTrigger value="preview" disabled={!compiled}>Preview</TabsTrigger>
          <TabsTrigger value="json" disabled={!compiled}>JSON</TabsTrigger>
        </TabsList>

        {/* ── Sources Tab ── */}
        <TabsContent value="sources">
          {loadingSources ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {sources.map((s) => {
                const meta = SOURCE_META[s.source] ?? { label: s.source, icon: Settings2, description: '' };
                const Icon = meta.icon;
                return (
                  <Card key={s.source}>
                    <CardContent className="py-4 px-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-muted rounded">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{meta.label}</span>
                            <StatusIcon status={s.status} />
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{meta.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-[10px]">
                              {s.count} items
                            </Badge>
                            <Badge variant={s.status === 'ok' ? 'default' : 'secondary'} className="text-[10px]">
                              {s.status}
                            </Badge>
                          </div>
                          {s.error && (
                            <p className="text-[10px] text-red-500 mt-1 truncate">{s.error}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Preview Tab ── */}
        <TabsContent value="preview">
          {compiled && (
            <div className="space-y-4">
              {/* Validation Status */}
              <Card>
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {compiled.validation.valid ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="text-sm font-medium text-green-700 dark:text-green-400">Valid</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="text-sm font-medium text-red-700 dark:text-red-400">Invalid</span>
                        </>
                      )}
                      <span className="text-xs text-muted-foreground">
                        Compiled {new Date(compiled.compiledAt).toLocaleString()}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      onClick={handlePublish}
                      disabled={!compiled.validation.valid || publishing}
                    >
                      {publishing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
                      Publish
                    </Button>
                  </div>
                  {!compiled.validation.valid && compiled.validation.errors.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {compiled.validation.errors.map((e, i) => (
                        <li key={i} className="text-xs text-red-500">• {e}</li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              {/* Config Summary */}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { label: 'Menu Items', value: (compiled.config.menu as unknown[])?.length ?? 0 },
                  { label: 'Pages', value: (compiled.config.pages as unknown[])?.length ?? 0 },
                  { label: 'Capabilities', value: (compiled.config.capabilities as unknown[])?.length ?? 0 },
                  { label: 'Feature Flags', value: Object.keys((compiled.config.featureFlags as Record<string, unknown>) ?? {}).length },
                ].map((item) => (
                  <Card key={item.label}>
                    <CardContent className="py-3 px-4 text-center">
                      <div className="text-2xl font-bold">{item.value}</div>
                      <div className="text-xs text-muted-foreground">{item.label}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Source Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Source Status</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="space-y-2">
                    {compiled.sources.map((s) => {
                      const meta = SOURCE_META[s.source] ?? { label: s.source, icon: Settings2 };
                      return (
                        <div key={s.source} className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2">
                            <StatusIcon status={s.status} />
                            <span>{meta.label}</span>
                          </div>
                          <Badge variant="outline" className="text-[10px]">{s.count}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* ── JSON Tab ── */}
        <TabsContent value="json">
          {compiled && (
            <Card>
              <CardContent className="p-0">
                <pre className="text-xs font-mono p-4 max-h-[600px] overflow-auto bg-muted/30 rounded">
                  {JSON.stringify(compiled.config, null, 2)}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
